import { FC, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Grid, Typography } from '@mui/material';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledDateField from '../../../../components/controlledComponents/ControlledDateField.component';
import ControlledFileUploadField from '../../../../components/controlledComponents/ControlledFileUploadField.component';
import {
  MaterialCategory,
  MaterialStatus,
  MaterialCondition,
} from '../../../../../apis/academics/academicsMaterials.interfaces';
import { TDocument } from '../../../../../apis/documents/documents.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TKeyValue } from 'src/utils/commonInterfaces';
import { useAcademicsMaterialsState } from '../../contexts/academicsMaterials.context';
import { useAcademicsMaterialsActions } from '../../contexts/academicsMaterials.context';
import dayjs, { Dayjs } from 'dayjs';
import { useAcademicsService } from 'src/apis/academics/useAcademicsService';
import { useQueryClient } from '@tanstack/react-query';
import { getAcademicsMaterialsKey } from '../../hooks/useAcademicsMaterialsHook';
import {
  isPickerDateRangeOrdered,
  zNumberFromInput,
  zOptionalNullablePickerDate,
  zRequiredPickerDate,
} from 'src/utils/zodFormHelpers';

type MaterialFormData = {
  name: string;
  description: string;
  category: MaterialCategory;
  status: MaterialStatus;
  condition: MaterialCondition;
  purchaseDate: Dayjs | null;
  lastMaintenanceDate?: Dayjs | null;
  location: string;
  quantity: number;
  minimumQuantity: number;
  supplier?: string;
  cost: number;
  tags: string;
  department: string;
  notes?: string;
  documents: {
    receipts: {
      documentName: string;
      title?: string;
      description?: string;
      verificationDate?: string;
      status: string;
    }[];
    invoices: {
      documentName: string;
      title?: string;
      description?: string;
      verificationDate?: string;
      status: string;
    }[];
    payments: {
      documentName: string;
      title?: string;
      description?: string;
      verificationDate?: string;
      status: string;
    }[];
  };
  tempReceiptDoc?: {
    documentName: string;
    title?: string;
    description?: string;
    verificationDate?: string;
    status: string;
  } | null;
  tempInvoiceDoc?: {
    documentName: string;
    title?: string;
    description?: string;
    verificationDate?: string;
    status: string;
  } | null;
  tempPaymentDoc?: {
    documentName: string;
    title?: string;
    description?: string;
    verificationDate?: string;
    status: string;
  } | null;
};

const getMaterialFormSchema = (translateText: (key: string) => string) =>
  z
    .object({
    name: z.string().min(1, translateText('common.fieldRequired')),
    description: z.string().min(1, translateText('common.fieldRequired')),
    category: z.nativeEnum(MaterialCategory, {
      required_error: translateText('common.fieldRequired'),
    }),
    status: z.nativeEnum(MaterialStatus, {
      required_error: translateText('common.fieldRequired'),
    }),
    condition: z.nativeEnum(MaterialCondition, {
      required_error: translateText('common.fieldRequired'),
    }),
    purchaseDate: zRequiredPickerDate({
      requiredMessage: translateText('common.fieldRequired'),
      invalidMessage: translateText('common.dateInvalid'),
    }),
    lastMaintenanceDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    location: z.string().min(1, translateText('common.fieldRequired')),
    quantity: zNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    minimumQuantity: zNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    supplier: z.string().optional(),
    cost: zNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    tags: z.string().optional(),
    department: z.string().min(1, translateText('common.fieldRequired')),
    notes: z.string().optional(),
    documents: z
      .object({
        receipts: z.array(z.any()).optional(),
        invoices: z.array(z.any()).optional(),
        payments: z.array(z.any()).optional(),
      })
      .optional(),
    tempReceiptDoc: z.any().optional().nullable(),
    tempInvoiceDoc: z.any().optional().nullable(),
    tempPaymentDoc: z.any().optional().nullable(),
  })
    .refine(
      (data) =>
        !data.lastMaintenanceDate ||
        isPickerDateRangeOrdered(data.purchaseDate, data.lastMaintenanceDate),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['lastMaintenanceDate'],
      }
    );

const getCategoryOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: MaterialCategory.BOOK,
      value: translateText('academics.materials.categoryOptions.book') || 'Book',
    },
    {
      key: MaterialCategory.LAB_EQUIPMENT,
      value:
        translateText('academics.materials.categoryOptions.labEquipment') ||
        'Lab Equipment',
    },
    {
      key: MaterialCategory.DIGITAL_RESOURCE,
      value:
        translateText('academics.materials.categoryOptions.digitalResource') ||
        'Digital Resource',
    },
    {
      key: MaterialCategory.STATIONERY,
      value:
        translateText('academics.materials.categoryOptions.stationery') ||
        'Stationery',
    },
    {
      key: MaterialCategory.OTHER,
      value:
        translateText('academics.materials.categoryOptions.other') || 'Other',
    },
  ];

const getStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: MaterialStatus.AVAILABLE,
      value:
        translateText('academics.materials.statuses.available') || 'Available',
    },
    {
      key: MaterialStatus.IN_USE,
      value: translateText('academics.materials.statuses.inUse') || 'In Use',
    },
    {
      key: MaterialStatus.MAINTENANCE,
      value:
        translateText('academics.materials.statuses.maintenance') ||
        'Maintenance',
    },
    {
      key: MaterialStatus.RETIRED,
      value: translateText('academics.materials.statuses.retired') || 'Retired',
    },
    {
      key: MaterialStatus.LOST,
      value: translateText('academics.materials.statuses.lost') || 'Lost',
    },
    {
      key: MaterialStatus.DAMAGED,
      value: translateText('academics.materials.statuses.damaged') || 'Damaged',
    },
  ];

const getConditionOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: MaterialCondition.NEW,
      value: translateText('academics.materials.conditions.new') || 'New',
    },
    {
      key: MaterialCondition.LIKE_NEW,
      value:
        translateText('academics.materials.conditions.likeNew') || 'Like New',
    },
    {
      key: MaterialCondition.GOOD,
      value: translateText('academics.materials.conditions.good') || 'Good',
    },
    {
      key: MaterialCondition.FAIR,
      value: translateText('academics.materials.conditions.fair') || 'Fair',
    },
    {
      key: MaterialCondition.POOR,
      value: translateText('academics.materials.conditions.poor') || 'Poor',
    },
  ];

const AddEditMaterialsView: FC = () => {
  const { translateText } = useTranslation();
  const { academicsMaterialsAppState } = useAcademicsMaterialsState();
  const {
    setIsManageMaterialOpen,
    setIsMaterialLoading,
    setMaterialErrorMessage,
  } = useAcademicsMaterialsActions();

  const {
    isManageMaterialOpen,
    selectedMaterial,
    isCreateMode,
    isMaterialLoading,
    materialErrorMessage,
  } = academicsMaterialsAppState;

  const materialFormSchema = getMaterialFormSchema(translateText);

  const defaultValues: MaterialFormData = {
    name: '',
    description: '',
    category: MaterialCategory.OTHER,
    status: MaterialStatus.AVAILABLE,
    condition: MaterialCondition.NEW,
    purchaseDate: dayjs(),
    lastMaintenanceDate: null,
    location: '',
    quantity: 0,
    minimumQuantity: 0,
    supplier: '',
    cost: 0,
    tags: '',
    department: '',
    notes: '',
    documents: {
      receipts: [],
      invoices: [],
      payments: [],
    },
    tempReceiptDoc: null,
    tempInvoiceDoc: null,
    tempPaymentDoc: null,
  };

  const methods = useForm<MaterialFormData>({
    resolver: zodResolver(materialFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = methods;

  const documents = watch('documents');
  const tempReceiptDoc = watch('tempReceiptDoc');
  const tempInvoiceDoc = watch('tempInvoiceDoc');
  const tempPaymentDoc = watch('tempPaymentDoc');

  useEffect(() => {
    if (tempReceiptDoc?.documentName) {
      const currentDocs = documents || {
        receipts: [],
        invoices: [],
        payments: [],
      };
      const newDoc = {
        documentName: tempReceiptDoc.documentName || '',
        title: tempReceiptDoc.title || tempReceiptDoc.documentName || '',
        description: tempReceiptDoc.description || '',
        verificationDate: tempReceiptDoc.verificationDate || '',
        status: tempReceiptDoc.status || '',
      };
      setValue(
        'documents',
        {
          ...currentDocs,
          receipts: [...(currentDocs.receipts || []), newDoc],
        },
        { shouldValidate: false }
      );
      setValue('tempReceiptDoc', null, { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempReceiptDoc?.documentName, setValue, documents]);

  useEffect(() => {
    if (tempInvoiceDoc?.documentName) {
      const currentDocs = documents || {
        receipts: [],
        invoices: [],
        payments: [],
      };
      const newDoc = {
        documentName: tempInvoiceDoc.documentName || '',
        title: tempInvoiceDoc.title || tempInvoiceDoc.documentName || '',
        description: tempInvoiceDoc.description || '',
        verificationDate: tempInvoiceDoc.verificationDate || '',
        status: tempInvoiceDoc.status || '',
      };
      setValue(
        'documents',
        {
          ...currentDocs,
          invoices: [...(currentDocs.invoices || []), newDoc],
        },
        { shouldValidate: false }
      );
      setValue('tempInvoiceDoc', null, { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempInvoiceDoc?.documentName, setValue, documents]);

  useEffect(() => {
    if (tempPaymentDoc?.documentName) {
      const currentDocs = documents || {
        receipts: [],
        invoices: [],
        payments: [],
      };
      const newDoc = {
        documentName: tempPaymentDoc.documentName || '',
        title: tempPaymentDoc.title || tempPaymentDoc.documentName || '',
        description: tempPaymentDoc.description || '',
        verificationDate: tempPaymentDoc.verificationDate || '',
        status: tempPaymentDoc.status || '',
      };
      setValue(
        'documents',
        {
          ...currentDocs,
          payments: [...(currentDocs.payments || []), newDoc],
        },
        { shouldValidate: false }
      );
      setValue('tempPaymentDoc', null, { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempPaymentDoc?.documentName, setValue, documents]);

  useEffect(() => {
    if (selectedMaterial && isManageMaterialOpen) {
      const existingDocs = selectedMaterial.documents || {
        receipts: [],
        invoices: [],
        payments: [],
      };
      reset({
        name: selectedMaterial.name || '',
        description: selectedMaterial.description || '',
        category: selectedMaterial.category || MaterialCategory.OTHER,
        status: selectedMaterial.status || MaterialStatus.AVAILABLE,
        condition: selectedMaterial.condition || MaterialCondition.NEW,
        purchaseDate: selectedMaterial.purchaseDate
          ? dayjs(selectedMaterial.purchaseDate)
          : dayjs(),
        lastMaintenanceDate: selectedMaterial.lastMaintenanceDate
          ? dayjs(selectedMaterial.lastMaintenanceDate)
          : null,
        location: selectedMaterial.location || '',
        quantity: selectedMaterial.quantity || 0,
        minimumQuantity: (selectedMaterial as any).minimumQuantity || 0,
        supplier: (selectedMaterial as any).supplier || '',
        cost: (selectedMaterial as any).cost || 0,
        tags: Array.isArray((selectedMaterial as any).tags)
          ? (selectedMaterial as any).tags.join(', ')
          : '',
        department: selectedMaterial.department || '',
        notes: selectedMaterial.notes || '',
        documents: {
          receipts: existingDocs.receipts.map((doc) => ({
            documentName: doc.documentName || '',
            title: doc.documentName || '',
            description: doc.description || '',
            verificationDate: doc.verificationDate || '',
            status: doc.status || '',
          })),
          invoices: existingDocs.invoices.map((doc) => ({
            documentName: doc.documentName || '',
            title: doc.documentName || '',
            description: doc.description || '',
            verificationDate: doc.verificationDate || '',
            status: doc.status || '',
          })),
          payments: existingDocs.payments.map((doc) => ({
            documentName: doc.documentName || '',
            title: doc.documentName || '',
            description: doc.description || '',
            verificationDate: doc.verificationDate || '',
            status: doc.status || '',
          })),
        },
        tempReceiptDoc: null,
        tempInvoiceDoc: null,
        tempPaymentDoc: null,
      });
    } else if (isManageMaterialOpen) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMaterial, isManageMaterialOpen, reset]);

  const { createAcademicsMaterialsGroup, patchAcademicsMaterialsGroup } = useAcademicsService();
  const queryClient = useQueryClient();

  const handleClose = () => {
    setIsManageMaterialOpen(false);
    setMaterialErrorMessage('');
    reset(defaultValues);
  };

  const onSubmitForm = async (data: MaterialFormData) => {
    setIsMaterialLoading(true);
    setMaterialErrorMessage('');

    try {
      // const tagsArray = data.tags
      //   ? data.tags.split(',').map((tag) => tag.trim()).filter((tag) => tag.length > 0)
      //   : [];

      const receipts: TDocument[] = (data.documents?.receipts || []).map(
        (doc) => ({
          documentId: '',
          userId: '',
          documentType: 'other' as any,
          documentName: doc.documentName,
          submissionDate: new Date().toISOString().split('T')[0],
          verificationDate: doc.verificationDate,
          status: doc.status as any,
          description: doc.description,
          uploadedAt: {
            userId: '',
            timestamp: Date.now(),
          },
        })
      );

      const invoices: TDocument[] = (data.documents?.invoices || []).map(
        (doc) => ({
          documentId: '',
          userId: '',
          documentType: 'other' as any,
          documentName: doc.documentName,
          submissionDate: new Date().toISOString().split('T')[0],
          verificationDate: doc.verificationDate,
          status: doc.status as any,
          description: doc.description,
          uploadedAt: {
            userId: '',
            timestamp: Date.now(),
          },
        })
      );

      const payments: TDocument[] = (data.documents?.payments || []).map(
        (doc) => ({
          documentId: '',
          userId: '',
          documentType: 'other' as any,
          documentName: doc.documentName,
          submissionDate: new Date().toISOString().split('T')[0],
          verificationDate: doc.verificationDate,
          status: doc.status as any,
          description: doc.description,
          uploadedAt: {
            userId: '',
            timestamp: Date.now(),
          },
        })
      );

      const materialData = {
        instituteId: '00000000-0000-0000-0000-000000000000', // TODO: Get from context/auth
        name: data.name,
        description: data.description,
        category: data.category,
        status: data.status,
        condition: data.condition,
        purchaseDate: data.purchaseDate,
        lastMaintenanceDate: data.lastMaintenanceDate,
        location: data.location,
        quantity: data.quantity,
        minimumQuantity: data.minimumQuantity,
        supplier: data.supplier,
        cost: data.cost,
        tags: data.tags ? data.tags.split(',').map((tag) => tag.trim()).filter((tag) => tag.length > 0) : [],
        department: data.department,
        notes: data.notes || undefined,
        documents: {
          receipts,
          invoices,
          payments,
        },
      };

      // Call API to create or update material group
      if (selectedMaterial?.id) {
        await patchAcademicsMaterialsGroup(selectedMaterial.id, materialData);
      } else {
        await createAcademicsMaterialsGroup(materialData);
      }

      // Invalidate and refetch materials list
      queryClient.invalidateQueries({ queryKey: [getAcademicsMaterialsKey] });

      setIsMaterialLoading(false);
      handleClose();
    } catch (error) {
      setIsMaterialLoading(false);
      setMaterialErrorMessage(
        error instanceof Error
          ? error.message
          : translateText('common.error') || 'An error occurred'
      );
    }
  };

  return (
    <div data-testid="add-edit-materials-container">
      <StructuredModal
        isOpen={isManageMaterialOpen}
        onClose={handleClose}
        title={
          isCreateMode
            ? translateText('academics.materials.addMaterial') || 'Add Material'
            : translateText('academics.materials.editMaterial') ||
            'Edit Material'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          isCreateMode
            ? translateText('common.save')
            : translateText('common.update')
        }
        isLoading={isMaterialLoading}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          materialErrorMessage ? (
            <Typography
              color="error"
              variant="body2"
              data-testid="material-error-message"
            >
              {materialErrorMessage}
            </Typography>
          ) : null
        }
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="material-form"
          >
            <Grid
              container
              spacing={{ xs: 2, sm: 3 }}
              padding={{ xs: 1, sm: 2 }}
              data-testid="material-form-container"
            >
              <Grid item xs={12} data-testid="material-details-section">
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  {translateText('academics.materials.materialDetails') ||
                    'Material Details'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="material-name-field"
              >
                <ControlledTextField
                  control={control}
                  name="name"
                  id="material-name"
                  label={
                    translateText('academics.materials.columns.name') || 'Name'
                  }
                  errorMessage={errors.name?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="material-category-field"
              >
                <ControlledSelectField
                  control={control}
                  name="category"
                  id="material-category"
                  label={
                    translateText('academics.materials.columns.category') ||
                    'Category'
                  }
                  itemList={getCategoryOptions(translateText)}
                  errorMessage={errors.category?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="material-description-field">
                <ControlledTextField
                  control={control}
                  name="description"
                  id="material-description"
                  label={translateText('common.description') || 'Description'}
                  errorMessage={errors.description?.message}
                  required
                  isGridItem={false}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="material-status-field"
              >
                <ControlledSelectField
                  control={control}
                  name="status"
                  id="material-status"
                  label={
                    translateText('academics.materials.columns.status') ||
                    'Status'
                  }
                  itemList={getStatusOptions(translateText)}
                  errorMessage={errors.status?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="material-condition-field"
              >
                <ControlledSelectField
                  control={control}
                  name="condition"
                  id="material-condition"
                  label={
                    translateText('academics.materials.columns.condition') ||
                    'Condition'
                  }
                  itemList={getConditionOptions(translateText)}
                  errorMessage={errors.condition?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="material-purchase-date-field"
              >
                <ControlledDateField
                  control={control}
                  name="purchaseDate"
                  id="material-purchase-date"
                  label={
                    translateText('academics.materials.columns.purchaseDate') ||
                    'Purchase Date'
                  }
                  errorMessage={errors.purchaseDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="material-last-maintenance-date-field"
              >
                <ControlledDateField
                  control={control}
                  name="lastMaintenanceDate"
                  id="material-last-maintenance-date"
                  label={
                    translateText('academics.materials.lastMaintenanceDate') ||
                    'Last Maintenance Date'
                  }
                  errorMessage={errors.lastMaintenanceDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="material-location-field"
              >
                <ControlledTextField
                  control={control}
                  name="location"
                  id="material-location"
                  label={
                    translateText('academics.materials.columns.location') ||
                    'Location'
                  }
                  errorMessage={errors.location?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="material-quantity-field"
              >
                <ControlledTextField
                  control={control}
                  name="quantity"
                  id="material-quantity"
                  label={
                    translateText('academics.materials.columns.quantity') ||
                    'Quantity'
                  }
                  errorMessage={errors.quantity?.message}
                  required
                  isGridItem={false}
                  type="number"
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="material-minimum-quantity-field"
              >
                <ControlledTextField
                  control={control}
                  name="minimumQuantity"
                  id="material-minimum-quantity"
                  label={
                    translateText('academics.materials.minimumQuantity') ||
                    'Minimum Quantity'
                  }
                  errorMessage={errors.minimumQuantity?.message}
                  required
                  isGridItem={false}
                  type="number"
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="material-supplier-field"
              >
                <ControlledTextField
                  control={control}
                  name="supplier"
                  id="material-supplier"
                  label={
                    translateText('academics.materials.columns.supplier') ||
                    'Supplier'
                  }
                  errorMessage={errors.supplier?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} data-testid="material-cost-field">
                <ControlledTextField
                  control={control}
                  name="cost"
                  id="material-cost"
                  label={
                    translateText('academics.materials.columns.cost') || 'Cost'
                  }
                  errorMessage={errors.cost?.message}
                  required
                  isGridItem={false}
                  type="number"
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="material-department-field"
              >
                <ControlledTextField
                  control={control}
                  name="department"
                  id="material-department"
                  label={
                    translateText('academics.materials.department') ||
                    'Department'
                  }
                  errorMessage={errors.department?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} data-testid="material-tags-field">
                <ControlledTextField
                  control={control}
                  name="tags"
                  id="material-tags"
                  label={
                    translateText('academics.materials.tags') ||
                    'Tags (comma-separated)'
                  }
                  errorMessage={errors.tags?.message}
                  isGridItem={false}
                  helperText={
                    translateText('academics.materials.tagsHelper') ||
                    'Separate multiple tags with commas'
                  }
                />
              </Grid>

              <Grid item xs={12} data-testid="material-notes-field">
                <ControlledTextField
                  control={control}
                  name="notes"
                  id="material-notes"
                  label={translateText('common.remarks') || 'Notes'}
                  errorMessage={errors.notes?.message}
                  isGridItem={false}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} data-testid="documents-section">
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText('academics.materials.documents') ||
                    'Documents'}
                </Typography>
              </Grid>

              <Grid item xs={12} data-testid="receipts-upload-section">
                <ControlledFileUploadField
                  control={control}
                  name="tempReceiptDoc.documentName"
                  id="material-receipt-upload"
                  label={
                    translateText('academics.materials.uploadReceipt') ||
                    'Upload Receipt'
                  }
                  sectionTitle={
                    translateText('academics.materials.receipts') || 'Receipts'
                  }
                  errorMessage={errors.tempReceiptDoc?.documentName?.message}
                />
              </Grid>

              <Grid item xs={12} data-testid="invoices-upload-section">
                <ControlledFileUploadField
                  control={control}
                  name="tempInvoiceDoc.documentName"
                  id="material-invoice-upload"
                  label={
                    translateText('academics.materials.uploadInvoice') ||
                    'Upload Invoice'
                  }
                  sectionTitle={
                    translateText('academics.materials.invoices') || 'Invoices'
                  }
                  errorMessage={errors.tempInvoiceDoc?.documentName?.message}
                />
              </Grid>

              <Grid item xs={12} data-testid="payments-upload-section">
                <ControlledFileUploadField
                  control={control}
                  name="tempPaymentDoc.documentName"
                  id="material-payment-upload"
                  label={
                    translateText('academics.materials.uploadPayment') ||
                    'Upload Payment'
                  }
                  sectionTitle={
                    translateText('academics.materials.payments') || 'Payments'
                  }
                  errorMessage={errors.tempPaymentDoc?.documentName?.message}
                />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditMaterialsView;
